import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, useInView } from 'framer-motion';
import {
  ArrowRight, Shield, ClipboardList, BarChart2, AlertTriangle,
  Database, Sparkles, Users, CheckCircle, FileText, Settings,
  Layers, Cpu, Zap, GitMerge, Lock, Upload, Search, Download, PackageCheck
} from 'lucide-react';
import SiteNavbar from '../components/SiteNavbar';
import AIAssistant from '../components/AIAssistant';

function Section({ children, className = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.section>
  );
}

function Badge({ children, variant = 'primary' }) {
  const base = 'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold tracking-wide uppercase';
  const variants = {
    primary: 'bg-primary/10 text-primary border border-primary/20',
    accent: 'bg-accent/15 text-amber-700 border border-accent/30',
    dark: 'bg-white/10 text-white/80 border border-white/20',
  };
  return <span className={`${base} ${variants[variant]}`}>{children}</span>;
}

export default function Platform() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'ar';
  const lang = i18n.language;

  const features = [
    {
      icon: Users,
      titleEn: 'Governance & Roles',
      titleAr: 'الحوكمة والأدوار',
      descEn: 'Three-tier role system: Admin controls platform config, Leader manages projects, Engineer executes tasks — every action permission-controlled.',
      descAr: 'نظام أدوار ثلاثي المستويات: المشرف يتحكم بإعدادات المنصة، القائد يدير المشاريع، المهندس ينفذ المهام — كل إجراء محكوم بالصلاحيات.',
      color: 'text-primary',
      bg: 'bg-primary/10',
      border: 'border-primary/20',
      badge: lang === 'ar' ? 'Admin · Leader · Engineer' : 'Admin · Leader · Engineer',
    },
    {
      icon: ClipboardList,
      titleEn: 'Audit Trail',
      titleAr: 'سجل المراجعة',
      descEn: 'Every mapping decision, compliance check, and approval action is timestamped and attributed — full traceability for disputes and compliance audits.',
      descAr: 'كل قرار ربط وفحص امتثال وإجراء موافقة موثق بطابع زمني ومنسوب — تتبع كامل للنزاعات وتدقيق الامتثال.',
      color: 'text-emerald-500',
      bg: 'bg-emerald-50',
      border: 'border-emerald-200',
      badge: lang === 'ar' ? 'قابل للتتبع الكامل' : 'Fully traceable',
    },
    {
      icon: BarChart2,
      titleEn: 'Confidence Scoring',
      titleAr: 'تقييم الثقة',
      descEn: 'Every AI output carries a confidence score. High-confidence items process automatically; low-confidence items route to structured human review queues.',
      descAr: 'كل مخرج من الذكاء الاصطناعي يحمل تقييم ثقة. البنود عالية الثقة تعالج تلقائياً؛ منخفضة الثقة تُوجَّه لطوابير مراجعة بشرية منظمة.',
      color: 'text-blue-500',
      bg: 'bg-sky-50',
      border: 'border-sky-200',
      badge: lang === 'ar' ? 'مرتفع / منخفض / استثناء' : 'High / Low / Exception',
    },
    {
      icon: AlertTriangle,
      titleEn: 'Exception Handling',
      titleAr: 'معالجة الاستثناءات',
      descEn: 'Structured exception queues ensure no item falls through the cracks — engineers review, decide, and their decisions feed back to improve future automation.',
      descAr: 'طوابير استثناء منظمة تضمن عدم ضياع أي بند — المهندسون يراجعون ويقررون وقراراتهم تغذي تحسين الأتمتة المستقبلية.',
      color: 'text-amber-500',
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      badge: lang === 'ar' ? 'لا شيء يفوت' : 'Nothing falls through',
    },
    {
      icon: Database,
      titleEn: 'Integration Ready',
      titleAr: 'جاهز للتكامل',
      descEn: 'Native connectors for Odoo, SAP, Oracle, and Microsoft Dynamics — outputs are field-mapped and import-ready with zero manual transformation.',
      descAr: 'موصلات أصلية لـ Odoo وSAP وOracle وMicrosoft Dynamics — المخرجات معيّنة الحقول وجاهزة للاستيراد دون أي تحويل يدوي.',
      color: 'text-purple-500',
      bg: 'bg-purple-50',
      border: 'border-purple-200',
      badge: 'Odoo · SAP · Oracle · Dynamics',
    },
    {
      icon: Sparkles,
      titleEn: 'AI Assistant Layer',
      titleAr: 'طبقة مساعد الذكاء الاصطناعي',
      descEn: 'Embedded conversational AI assistant for onboarding, workflow guidance, and intelligent Q&A — available in Arabic and English.',
      descAr: 'مساعد ذكاء اصطناعي محادثي مدمج للتأهيل وإرشاد سير العمل والأسئلة الذكية — متاح بالعربية والإنجليزية.',
      color: 'text-accent',
      bg: 'bg-accent/8',
      border: 'border-accent/20',
      badge: 'AR · EN',
    },
  ];

  const erpPlatforms = [
    {
      name: 'Odoo',
      descEn: 'Open-source ERP with full BoQ and purchase order module integration',
      descAr: 'ERP مفتوح المصدر مع تكامل كامل لوحدات قوائم الكميات وأوامر الشراء',
    },
    {
      name: 'SAP',
      descEn: 'Enterprise SAP with MM and PM module field mapping for procurement',
      descAr: 'SAP المؤسسي مع تعيين حقول وحدات MM وPM للمشتريات',
    },
    {
      name: 'Oracle',
      descEn: 'Oracle ERP Cloud with direct import templates for purchasing workflows',
      descAr: 'Oracle ERP Cloud مع قوالب استيراد مباشر لسير عمل المشتريات',
    },
    {
      name: 'MS Dynamics',
      descEn: 'Microsoft Dynamics 365 integration for supply chain and procurement',
      descAr: 'تكامل Microsoft Dynamics 365 لسلاسل التوريد والمشتريات',
    },
  ];

  return (
    <div className={`min-h-screen bg-white ${isRTL ? 'font-arabic' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <SiteNavbar />
      <AIAssistant source="platform" />

      {/* ── HERO ── */}
      <section className="relative min-h-[80vh] flex items-center overflow-hidden bg-midnight -mt-16 pt-16">
        <div className="absolute inset-0 blueprint-grid-dark opacity-50" />
        <div className="absolute top-1/4 left-1/3 w-[600px] h-[400px] rounded-full bg-primary/12 blur-[130px] pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-[350px] h-[250px] rounded-full bg-accent/6 blur-[80px] pointer-events-none" />

        {/* Background image */}
        <div className={`absolute inset-y-0 ${isRTL ? 'left-0' : 'right-0'} w-1/2 hidden lg:block pointer-events-none`}>
          <img
            src="/industrial/engineering drawings 4.jpg"
            alt="Platform engineering"
            className="w-full h-full object-cover opacity-8"
          />
          <div className={`absolute inset-0 bg-gradient-to-r ${isRTL ? 'from-transparent to-midnight' : 'from-midnight to-transparent'}`} />
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-2xl">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <Badge variant="dark"><Shield size={12} />{lang === 'ar' ? 'المنصة المشتركة' : 'Shared Platform'}</Badge>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="mt-6 text-4xl sm:text-5xl lg:text-[3rem] font-black text-white leading-[1.06] tracking-tight"
            >
              {lang === 'ar' ? (
                <>أتمتة <span className="gradient-text">محكومة</span> في القلب</>
              ) : (
                <><span className="gradient-text">Governed</span> Automation at the Core</>
              )}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25 }}
              className="mt-6 text-base sm:text-lg text-white/60 leading-relaxed max-w-xl"
            >
              {lang === 'ar'
                ? 'منصة موحدة واحدة مع طبقة حوكمة مشتركة ومحرك سير عمل وسجل مراجعة — تُشغّل وحدتي قوائم الكميات والمستندات التقنية.'
                : 'One unified platform with shared governance layer, workflow engine, and audit trail — powering both the BoQ and Submittals modules.'}
            </motion.p>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.35 }}
              className="mt-8 flex flex-wrap gap-8"
            >
              {[
                { val: '6', label: lang === 'ar' ? 'ميزات منصة أساسية' : 'Core platform features' },
                { val: '4', label: lang === 'ar' ? 'تكاملات ERP' : 'ERP integrations' },
                { val: '2', label: lang === 'ar' ? 'وحدات صناعية' : 'Industrial modules' },
              ].map(({ val, label }, i) => (
                <div key={i}>
                  <p className="text-3xl font-black gradient-text">{val}</p>
                  <p className="text-white/45 text-xs mt-0.5">{label}</p>
                </div>
              ))}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.45 }}
              className="mt-8 flex flex-wrap gap-3"
            >
              <button
                onClick={() => navigate('/contact')}
                className="flex items-center gap-2 px-6 py-3 rounded-xl bg-primary text-white font-semibold text-sm hover:bg-primary-hover transition-all shadow-primary-glow glow-primary-hover"
              >
                {lang === 'ar' ? 'احجز عرضاً' : 'Book a Demo'}
                <ArrowRight size={16} className={isRTL ? 'rotate-180' : ''} />
              </button>
              <button
                onClick={() => navigate('/solutions')}
                className="flex items-center gap-2 px-6 py-3 rounded-xl bg-white/8 text-white font-semibold text-sm hover:bg-white/14 transition-all border border-white/20"
              >
                {lang === 'ar' ? 'استكشف الحلول' : 'Explore Solutions'}
              </button>
            </motion.div>
          </div>
        </div>

        {/* Scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5"
        >
          <span className="text-white/25 text-[10px] tracking-widest uppercase">Scroll</span>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.6, repeat: Infinity }}
            className="w-px h-8 bg-gradient-to-b from-white/25 to-transparent"
          />
        </motion.div>
      </section>

      {/* ── 6 PLATFORM FEATURES ── */}
      <Section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge variant="primary"><Layers size={12} />{lang === 'ar' ? 'ميزات المنصة' : 'Platform Features'}</Badge>
            <h2 className="mt-4 text-3xl sm:text-4xl font-black text-heading">
              {lang === 'ar' ? 'ست ركائز للمنصة الموحدة' : 'Six Pillars of the Unified Platform'}
            </h2>
            <p className="mt-3 text-body text-sm max-w-xl mx-auto">
              {lang === 'ar'
                ? 'كل ميزة مصممة لضمان الاتساق والأمان والقابلية للتتبع عبر كل عملية صناعية.'
                : 'Every feature engineered to ensure consistency, security, and traceability across every industrial operation.'}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className={`bg-white rounded-2xl p-6 border ${f.border} card-hover`}
              >
                <div className={`w-12 h-12 rounded-xl ${f.bg} flex items-center justify-center mb-5`}>
                  <f.icon size={22} className={f.color} />
                </div>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <h3 className="text-heading font-bold text-base">{lang === 'ar' ? f.titleAr : f.titleEn}</h3>
                  <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase ${f.bg} ${f.color} border ${f.border} whitespace-nowrap`}>
                    {f.badge}
                  </span>
                </div>
                <p className="text-body text-xs leading-relaxed">{lang === 'ar' ? f.descAr : f.descEn}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* ── CTA ── */}
      <Section className="py-24 bg-white relative overflow-hidden">
        <div className="absolute inset-0 blueprint-grid opacity-50" />
        <div className="absolute top-0 right-1/3 w-72 h-72 rounded-full bg-primary/5 blur-[80px]" />
        <div className="relative z-10 max-w-3xl mx-auto text-center px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl font-black text-heading">
            {lang === 'ar' ? 'جاهز لرؤية المنصة عملياً؟' : 'Ready to See the Platform in Action?'}
          </h2>
          <p className="mt-4 text-body leading-relaxed text-sm max-w-xl mx-auto">
            {lang === 'ar'
              ? 'احجز عرضاً مباشراً وشاهد كيف تعمل طبقة الحوكمة والتكامل عبر وحدات قوائم الكميات والمستندات التقنية.'
              : 'Book a live demo and see the governance and integration layer working across BoQ and Submittals modules.'}
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <button
              onClick={() => navigate('/contact')}
              className="flex items-center gap-2 px-7 py-3.5 rounded-xl bg-primary text-white font-semibold hover:bg-primary-hover transition-all shadow-primary-glow glow-primary-hover"
            >
              {lang === 'ar' ? 'احجز عرضاً' : 'Book a Demo'}
              <ArrowRight size={16} className={isRTL ? 'rotate-180' : ''} />
            </button>
            <button
              onClick={() => navigate('/solutions')}
              className="flex items-center gap-2 px-7 py-3.5 rounded-xl border-2 border-border-light text-body font-semibold hover:border-primary/30 hover:text-primary transition-all"
            >
              {lang === 'ar' ? 'استكشف الحلول' : 'Explore Solutions'}
            </button>
          </div>
        </div>
      </Section>

      {/* ── FOOTER ── */}
      <footer className="bg-midnight border-t border-dark-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <img src="/tuly.png" alt="Tuly Technology" className="h-8 w-auto" />
                <span className="text-white font-bold text-sm">Tuly Technology</span>
              </div>
              <p className="text-muted text-xs leading-relaxed">{t('footer.tagline')}</p>
            </div>
            <div>
              <h4 className="text-white font-semibold text-xs mb-4 uppercase tracking-wide">{t('footer.solutions')}</h4>
              <ul className="space-y-2.5">
                {[['boq', '/solutions/boq'], ['submittals', '/solutions/submittals'], ['platform', '/platform']].map(([k, h]) => (
                  <li key={k}><button onClick={() => navigate(h)} className="text-muted hover:text-white text-xs transition-colors">{t(`footer.${k}`)}</button></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold text-xs mb-4 uppercase tracking-wide">{t('footer.company')}</h4>
              <ul className="space-y-2.5">
                {[['about', '/about'], ['contact', '/contact']].map(([k, h]) => (
                  <li key={k}><button onClick={() => navigate(h)} className="text-muted hover:text-white text-xs transition-colors">{t(`footer.${k}`)}</button></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold text-xs mb-4 uppercase tracking-wide">{t('footer.legal')}</h4>
              <ul className="space-y-2.5">
                {[['privacy', '/privacy'], ['terms', '/terms']].map(([k, h]) => (
                  <li key={k}><button onClick={() => navigate(h)} className="text-muted hover:text-white text-xs transition-colors">{t(`footer.${k}`)}</button></li>
                ))}
              </ul>
            </div>
          </div>
          <div className="mt-10 pt-6 border-t border-dark-border flex flex-col md:flex-row items-center justify-between gap-3">
            <p className="text-muted text-xs">© {new Date().getFullYear()} Tuly Technology. {t('footer.allRights')}</p>
            <p className="text-muted/50 text-xs">{t('footer.madeWith')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
